import { Box, styled, Typography } from '@mui/material'
import { ReactNode } from 'react'
import dayjs from 'dayjs'

const TimelineContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  margin: '0 auto',
  padding: theme.spacing(3)
}))

const SessionContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  position: 'relative'
}))

const SessionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  position: 'relative'
}))

const SessionDateChip = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primarySoft.main,
  color: theme.palette.primarySoft.contrastText,
  padding: theme.spacing(1, 2),
  borderRadius: theme.spacing(3),
  fontSize: '12pt',
  zIndex: 2
}))

const SessionItemsContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '50%',
    top: 0,
    bottom: 0,
    width: 2,
    backgroundColor: theme.palette.divider,
    transform: 'translateX(-50%)',
    zIndex: 1
  }
}))

const TimelineItemContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
  position: 'relative',
  '&:last-child': {
    marginBottom: 0
  }
}))

const TimelineDot = styled(Box)(({ theme }) => ({
  width: 16,
  height: 16,
  backgroundColor: theme.palette.primarySoft.main,
  borderRadius: '50%',
  zIndex: 2,
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)'
}))

const TimelineContent = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(1, 2),
  width: '50%'
}))

interface TimeLineProps<T> {
  timelineItems: Array<T>
  leftLabel: keyof T | ((item: T) => ReactNode)
  rightLabel: keyof T | ((item: T) => ReactNode)
  groupBy?: keyof T | ((item: T) => string)
  sessionDateFormat?: string
  key?: string
}

const TimeLineItemsView = <T,>({
  leftLabel,
  rightLabel,
  timelineItems,
  key
}: TimeLineProps<T>) => {
  return (
    <SessionItemsContainer>
      {timelineItems.map((item, index) => (
        <TimelineItemContainer
          key={key ? `${key}-${index}` : 'timeline-item-' + index}
        >
          <TimelineContent sx={{ textAlign: 'right', pr: 4 }}>
            {typeof leftLabel === 'function' ? (
              leftLabel(item)
            ) : (
              <Typography variant='body2' color='text.secondary'>
                {item[leftLabel] as unknown as ReactNode}
              </Typography>
            )}
          </TimelineContent>

          <TimelineDot />

          <TimelineContent sx={{ pl: 4 }}>
            {typeof rightLabel === 'function' ? (
              rightLabel(item)
            ) : (
              <Typography variant='body1' color='text.secondary'>
                {item[rightLabel] as unknown as ReactNode}
              </Typography>
            )}
          </TimelineContent>
        </TimelineItemContainer>
      ))}
    </SessionItemsContainer>
  )
}

const TimeLine = <T,>({
  timelineItems,
  leftLabel,
  rightLabel,
  groupBy,
  sessionDateFormat = 'DD/MM/YYYY'
}: TimeLineProps<T>) => {
  if (!groupBy) {
    return (
      <TimelineContainer>
        <TimeLineItemsView
          leftLabel={leftLabel}
          rightLabel={rightLabel}
          timelineItems={timelineItems}
        />
      </TimelineContainer>
    )
  }

  // Group the items by session
  const groupedItems = timelineItems.reduce(
    (groups, item) => {
      const groupKey =
        typeof groupBy === 'function' ? groupBy(item) : (groupBy as string)

      if (!groups[groupKey]) {
        groups[groupKey] = []
      }
      groups[groupKey].push(item)
      return groups
    },
    {} as Record<string, T[]>
  )

  return (
    <TimelineContainer>
      {Object.keys(groupedItems).map(groupKey => {
        const items = groupedItems[groupKey]
        const sessionDate = dayjs(groupKey).format(sessionDateFormat)

        return (
          <SessionContainer key={groupKey}>
            <SessionHeader>
              <SessionDateChip>{sessionDate}</SessionDateChip>
            </SessionHeader>

            <TimeLineItemsView
              leftLabel={leftLabel}
              rightLabel={rightLabel}
              timelineItems={items}
              key={'session-'}
            />
          </SessionContainer>
        )
      })}
    </TimelineContainer>
  )
}

export default TimeLine
